<template>
  <v-dialog
    v-model="showUserRegistrationDialog"
    max-width="1000px"
    persistent
    hide-overlay
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="primary white--text">
        <h3 class="headline mb-0">{{ $t("texts.openAccount") }}</h3>
        <v-spacer/>
        <v-btn text small fab @click="showUserRegistrationDialog = false">
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-stepper class="elevation-0" v-model="registrationStep">
          <v-stepper-header class="elevation-0">
            <v-stepper-step step="1" v-bind:complete="registrationStep > 1">{{ $t("texts.type") }}</v-stepper-step>
            <v-divider/>
            <v-stepper-step step="2" v-bind:complete="registrationStep > 2">{{ $t("texts.registration") }}</v-stepper-step>
          </v-stepper-header>
          <v-stepper-content step="1">
            <v-container class="pa-0">
              <v-row>
                <v-col :cols="12">
                  <p class="text-center">{{ $t("texts.typeQuestion") }}</p>
                </v-col>
              </v-row>
              <v-row class="py-3">
                <v-col :cols="12" :sm="4" :offset-sm="1">
                  <v-card hover flat>
                    <v-card-text @click="selectCompany()">
                      <p class="text-center"><v-icon x-large left>business</v-icon></p>
                      <p class="text-center">{{ $t("texts.typeCompany") }}</p>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col :cols="12" :sm="4" :offset-sm="2">
                  <v-card hover flat>
                    <v-card-text @click="selectEmployee()">
                      <p class="text-center"><v-icon x-large right>person</v-icon></p>
                      <p class="text-center">{{ $t("texts.typeEmployee") }}</p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
          <v-stepper-content step="2">
            <form>
              <v-container class="py-0" grid-list-md>
                <v-row>
                  <v-col :cols="12">
                    <v-autocomplete
                      :items="salutationItems"
                      v-model="form.sex"
                      :label="$t('texts.salutation')"
                      item-text="text"
                      item-value="value"
                      required
                      :error-messages="errors.collect('salutation')"
                      v-validate="'required'"
                      data-vv-name="salutation"
                      :data-vv-as="$t('texts.salutation')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12" :sm="6">
                    <v-text-field
                      :label="$t('texts.firstName')"
                      v-model="form.first_name"
                      required
                      v-validate="'required'"
                      data-vv-name="first_name"
                      :data-vv-as="$t('texts.firstName')"
                      :error-messages="errors.collect('first_name')"
                    />
                  </v-col>
                  <v-col :cols="12" :sm="6">
                    <v-text-field
                      :label="$t('texts.lastName')"
                      v-model="form.last_name"
                      required
                      v-validate="'required'"
                      :error-messages="errors.collect('lastName')"
                      data-vv-name="lastName"
                      :data-vv-as="$t('texts.lastName')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-text-field
                      :label="$t('texts.email')"
                      v-model="form.email"
                      required
                      v-validate="'required|email'"
                      :error-messages="errors.collect('email')"
                      data-vv-name="email"
                      :data-vv-as="$t('texts.email')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12" :sm="6">
                    <v-text-field
                      :label="$t('texts.password')"
                      v-model="form.password"
                      :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
                      @click:append="() => (hidePassword = !hidePassword)"
                      :type="hidePassword ? 'password' : 'text'"
                      required
                      v-validate="'required|min:8|verify_password'"
                      :error-messages="errors.collect('password')"
                      data-vv-name="password"
                      :data-vv-as="$t('texts.password')"
                      ref=password
                      autocomplete="new-password"
                    />
                  </v-col>
                  <v-col :cols="12" :sm="6">
                    <v-text-field
                      type="password"
                      :label="$t('texts.passwordConfirmation')"
                      v-model="form.password_confirmation"
                      required
                      hide-details
                      v-validate="'required|confirmed:password'"
                      :error-messages="errors.collect('password_confirmation')"
                      data-vv-name="password_confirmation"
                      :data-vv-as="$t('texts.passwordConfirmation')"
                      autocomplete="new-password"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="2" :sm="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="form.agb_accepted_at"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      hide-details
                      required
                      v-validate="'required|included:1'"
                      data-vv-name="accepted_agb_at"
                      :data-vv-as="$t('texts.acceptAGBandPrivacyPolicy')"
                      :error-messages="errors.collect('accepted_agb_at')"
                      class="my-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col :cols="10" :sm="11" class="d-flex align-center">
                    <span
                      :class="{'error--text': errors.collect('accepted_agb_at').length > 0}"
                      v-html="$t('texts.acceptAGBandPrivacyPolicy')">
                    </span>
                  </v-col>
                </v-row>
                <v-row v-if="false">
                  <v-col :cols="2" :sm="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="form.dsg_accepted_at"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      hide-details
                      required
                      v-validate="'required|included:1'"
                      data-vv-name="dsg_accepted_at"
                      :data-vv-as="$t('texts.acceptPrivacyPolicy')"
                      :error-messages="errors.collect('dsg_accepted_at')"
                      class="my-0"
                    >
                    </v-checkbox>
                  </v-col>
                  <v-col :cols="10" :sm="11" class="d-flex align-center">
                    <span
                      :class="{
                        'error--text':
                        errors.collect('dsg_accepted_at').length > 0
                      }"
                      v-html="$t('texts.acceptPrivacyPolicy')">
                    </span>
                  </v-col>
                </v-row>
                <v-row v-if="!form.is_employee">
                  <v-col :cols="2" :sm="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="form.rules_accepted_at"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      hide-details
                      required
                      v-validate="'required|included:1'"
                      data-vv-name="rules_accepted_at"
                      :data-vv-as="$t('texts.acceptRules')"
                      :error-messages="errors.collect('rules_accepted_at')"
                      class="my-"
                    />
                  </v-col>
                  <v-col :cols="10" :sm="11" class="d-flex align-center">
                    <span
                      :class="{'error--text': errors.collect('rules_accepted_at').length > 0}"
                      v-html="$t('texts.acceptRules')">
                    </span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="2" :sm="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="form.newsletter"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      hide-details
                      class="my-0"
                    >
                    </v-checkbox>
                  </v-col>
                  <v-col :cols="10" :sm="11" class="d-flex align-center">
                    <span v-html="$t('texts.acceptAdvertising')"></span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-divider class="mt-3 mb-2" />
                  </v-col>
                </v-row>
                <v-row justify="space-between">
                  <v-col class="d-flex justify-space-between">
                    <v-btn
                      color="primary"
                      @click.native="registrationStep = 1"
                      text
                    >
                      {{ $t("texts.back") }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="white--text"
                      @click.stop.prevent="handleUserRegistrationFormSubmit()"
                      :disabled="buttonLoading"
                      :loading="buttonLoading"
                    >
                      {{ $t("texts.finishRegistration") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </form>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import { userRegistrationUrl } from '@/config';

const { mapState: mapUserState } = createNamespacedHelpers('user');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'UserRegistrationForm',
  data() {
    return {
      showUserRegistrationDialog: false,
      buttonLoading: false,
      registrationStep: 1,
      hidePassword: true,
      form: {},
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-user-registration-dialog', () => {
      this.reset();
      this.closeUserLoginDialog();
      this.showUserRegistrationDialog = true;
    });
    this.$eventBus.$on('close-user-registration-dialog', () => {
      this.showUserRegistrationDialog = false;
    });
  },
  computed: {
    ...mapUserState([
      'authUser',
      'language',
    ]),
    salutationItems() {
      const items = [
        { value: 1, text: this.$i18n.t('texts.salutationMr') },
        { value: 2, text: this.$i18n.t('texts.salutationMrs') },
      ];
      return items;
    },
  },
  methods: {
    ...mapMessageActions([
      'showMessage',
    ]),
    reset() {
      this.form = {
        email: '',
        password: '',
        password_confirmation: '',
        sex: '',
        first_name: '',
        last_name: '',
        is_employee: null,
        language: this.language,
        agb_accepted_at: 0,
        dsg_accepted_at: 1,
        rules_accepted_at: 0,
        newsletter: 1,
      };
      this.$validator.reset();
      this.registrationStep = 1;
    },
    selectCompany() {
      this.form.is_employee = false;
      this.registrationStep = 2;
    },
    selectEmployee() {
      this.form.is_employee = true;
      this.registrationStep = 2;
    },
    handleUserRegistrationFormSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$gtm.trackEvent({
            event: `complete-registration-${this.form.is_employee ? 'employee' : 'company'}`, // Event type [default = 'interaction'] (Optional)
            category: 'Registration',
            action: 'click',
            label: `Complete Registration ${this.form.is_employee ? 'Employee' : 'Company'}`,
            value: this.form.is_employee ? 'employee' : 'company',
            noninteraction: false, // Optional
          });
          this.buttonLoading = true;
          // TODO move to store
          Vue.axios.post(userRegistrationUrl, this.form)
            .then((response) => {
              if (response.status === 201) {
                this.$auth.login({
                  data: this.form,
                  rememberMe: true,
                  fetchUser: true,
                  success() {
                    this.buttonLoading = false;
                    this.$store.dispatch('user/setUserObject', this.authUser);
                    this.showUserRegistrationDialog = false;
                    this.$store.dispatch('user/setInRegistrationProcess', false);
                    if (this.authUser.is_employee) {
                      this.$router.push({ name: 'employee.profile' });
                    }
                    return true;
                  },
                  error() {
                    this.buttonLoading = false;
                    // TODO Error Message?
                    console.log('error');
                  },
                });
              }
            })
            .catch((error) => {
              this.buttonLoading = false;
              const errors = Object.keys(error.response.data.errors)[0];
              const errorText = error.response.data.errors[errors][0];
              this.showMessage({
                text: errorText,
                color: 'error',
              });
            });
        }
      });
    },
    checkIfAuthenticated() {
      return this.$auth.check();
    },
    closeUserLoginDialog() {
      this.$eventBus.$emit('close-user-login-dialog');
    },
  },
};
</script>
